// Que secciones del backoffice ve esta sesion, y hasta donde.
//
// Lo consumen cuatro lugares que tienen que decir lo mismo:
//   1. El menu lateral (AdminShell) — que links se dibujan.
//   2. Las rutas (App.tsx) — que pasa si alguien escribe la URL a mano.
//   3. El redirect post-login — a donde cae cada perfil al entrar.
//   4. Los botones que cambian algo — se esconden sin nivel completo.
//
// La respuesta no esta escrita aca: sale del mapa seccion → nivel que el
// perfil de la cuenta (Equipo → Perfiles) deja en la sesion al login. Este
// modulo solo lo lee. El catalogo de secciones (`@access`) es el mismo archivo
// que usan las Lambdas, asi que el portal y el backend no pueden discrepar
// sobre que secciones existen.
//
// Esto es UX, no seguridad: cada endpoint /admin/* revalida el nivel con
// requireAccess() del lado del backend (libs/admin/auth.ts).

import {
  ALL_HIDDEN,
  BUILTIN_ACCESS,
  canRead as mapCanRead,
  canWrite as mapCanWrite,
  type AccessMap,
  type AccessSection,
} from '@access';
import type { AdminSession } from '@/lib/api';

export type { AccessSection, AccessMap, AccessLevel } from '@access';
export { ACCESS_SECTIONS, SECTION_META } from '@access';

/**
 * Secciones del portal: las del catalogo mas dos que no se configuran por
 * perfil — Cupones (la abre el permiso por cuenta `danger_zone`) y Mi cuenta
 * (la ve cualquiera).
 */
export type Section = AccessSection | 'coupons' | 'account';

/** Ruta de cada seccion, para armar el menu y los redirects sin repetir strings. */
export const SECTION_PATH: Record<Section, string> = {
  dashboard: '/dashboard',
  users: '/users',
  partners: '/partners',
  catalog: '/catalog',
  reports: '/reports',
  invoices: '/invoices',
  jobs: '/jobs',
  audit: '/audit',
  disposable: '/disposable-domains',
  team: '/team',
  coupons: '/coupons',
  account: '/account',
};

/**
 * Mapa de acceso de la sesion. Una sesion guardada antes de que existieran
 * los perfiles no lo trae: para los tres originales se deduce del nombre, y
 * cualquier otro queda todo oculto hasta volver a entrar.
 */
export function accessOf(session: AdminSession | null): AccessMap {
  if (!session) return ALL_HIDDEN;
  if (session.access) return session.access;
  return (BUILTIN_ACCESS as Record<string, AccessMap | undefined>)[session.role] ?? ALL_HIDDEN;
}

/** ¿Esta sesion ve esta seccion? Sin sesion, no. */
export function canSee(session: AdminSession | null, section: Section): boolean {
  if (!session) return false;
  if (section === 'account') return true;
  if (section === 'coupons') return session.permissions?.['danger_zone'] === true;
  return mapCanRead(accessOf(session), section);
}

/** ¿Puede operar (crear, editar, ejecutar acciones) en esta seccion? */
export function canWrite(session: AdminSession | null, section: AccessSection): boolean {
  if (!session) return false;
  return mapCanWrite(accessOf(session), section);
}

// Orden en que se busca a donde mandar a alguien que entra (o que pidio una
// seccion que no le corresponde). El primero que pueda ver, gana.
const LANDING_ORDER: readonly Section[] = [
  'dashboard',
  'users',
  'invoices',
  'partners',
  'catalog',
  'reports',
  'jobs',
  'audit',
  'disposable',
  'team',
  'coupons',
  'account',
];

/**
 * Primera pantalla de la sesion. Un perfil que no viera ninguna seccion cae
 * en su cuenta, que siempre puede ver.
 */
export function landingPath(session: AdminSession | null): string {
  if (!session) return '/login';
  const first = LANDING_ORDER.find((s) => canSee(session, s));
  return SECTION_PATH[first ?? 'account'];
}
